/* A progressively enhanced, keyboard and touch accessible product rack. */
(() => {
  if (window.dmHalloweenLoaded) return;
  window.dmHalloweenLoaded = true;
  const mount = (root) => {
    if (root.dataset.hwMounted) return;
    root.dataset.hwMounted = 'true';
    const buttons = Array.from(root.querySelectorAll('[data-hw-select]'));
    const panels = Array.from(root.querySelectorAll('[data-hw-detail]'));
    const rail = root.querySelector('[data-hw-hangers]');
    const position = root.querySelector('[data-hw-position]');
    const steps = Array.from(root.querySelectorAll('[data-hw-go]'));
    const details = root.querySelector('.dm-hw__details');
    const dialog = root.querySelector('[data-hw-dialog]');
    const products = Array.from(root.querySelectorAll('[data-hw-product]'));
    let lastTrigger;
    if (!buttons.length || !rail) return;
    const mobile = matchMedia('(max-width: 749px)');
    const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)');
    let selected = Math.min(Number(root.dataset.initialIndex) || 0, buttons.length - 1);
    let scrollingToSelection = false;
    let scrollTimer;
    let postponeAuto = () => {};
    let syncPlayback = () => {};
    const center = (behavior = 'smooth') => {
      if (!mobile.matches) return;
      scrollingToSelection = true;
      const button = buttons[selected];
      rail.scrollTo({left: button.offsetLeft - (rail.clientWidth - button.offsetWidth) / 2, behavior: reduceMotion.matches ? 'instant' : behavior});
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => { scrollingToSelection = false; }, 500);
    };
    const select = (index, shouldCenter = true, automatic = false) => {
      if (details) details.setAttribute('aria-live', automatic ? 'off' : 'polite');
      const previous = selected;
      selected = (index + buttons.length) % buttons.length;
      buttons.forEach((button, i) => {
        button.classList.toggle('is-active', i === selected);
        button.setAttribute('aria-pressed', String(i === selected));
        const distance = i - selected;
        button.style.setProperty('--hw-yaw', '0deg');
        button.style.setProperty('--hw-depth', `${i === selected ? 14 : -8}px`);
        button.style.setProperty('--hw-roll', `${i === selected ? 0 : Math.sign(distance) * 2}deg`);
        button.style.setProperty('--hw-scale', i === selected ? '1.025' : '1');
        if (previous !== selected) button.classList.toggle('is-arriving', i === selected && !reduceMotion.matches);
      });
      steps.forEach((step, i) => {
        if (i === selected) step.setAttribute('aria-current', 'true');
        else step.removeAttribute('aria-current');
      });
      panels.forEach((panel, i) => { panel.hidden = i !== selected; });
      if (position) position.textContent = selected + 1;
      if (shouldCenter) center();
      if (!automatic) postponeAuto();
    };
    // Small pointer tilt makes the cloth pivot around its hook, not around the photo's center.
    const finePointer = matchMedia('(hover: hover) and (pointer: fine)');
    buttons.forEach((button) => {
      let tiltFrame;
      button.addEventListener('pointermove', (event) => {
        if (!finePointer.matches || reduceMotion.matches) return;
        const bounds = button.getBoundingClientRect();
        const x = Math.max(-1, Math.min(1, (event.clientX - bounds.left) / bounds.width * 2 - 1));
        cancelAnimationFrame(tiltFrame);
        tiltFrame = requestAnimationFrame(() => {
          button.style.setProperty('--hw-pointer-yaw', '0deg');
          button.style.setProperty('--hw-pointer-roll', `${x * 1.5}deg`);
        });
      });
      button.addEventListener('pointerleave', () => {
        cancelAnimationFrame(tiltFrame);
        button.style.setProperty('--hw-pointer-yaw', '0deg');
        button.style.setProperty('--hw-pointer-roll', '0deg');
      });
    });
    const openDetails = (index, trigger) => {
      if (!dialog || !products[index]) return;
      select(index, false);
      window.dmOverlay?.close();
      lastTrigger = trigger;
      products.forEach((product, i) => { product.hidden = i !== index; });
      dialog.setAttribute('aria-labelledby', products[index].querySelector('h2').id);
      dialog.showModal();
      syncPlayback();
      document.documentElement.classList.add('dm-hw-dialog-open');
      dialog.scrollTop = 0;
      dialog.querySelector('[data-hw-close]').focus({preventScroll: true});
      // Reuse the store's current size guide, including its gap warnings.
      const guide = products[index].querySelector('[data-hw-size-guide]');
      const source = document.querySelector('#dm-sg .dm-sg__table');
      if (guide && source && !guide.dataset.ready) {
        const target = guide.querySelector('[data-hw-size-table]');
        target.append(source.cloneNode(true));
        document.querySelectorAll('#dm-sg .dm-sg__warn').forEach((warning) => {
          const note = document.createElement('p');
          note.textContent = warning.textContent.trim();
          target.append(note);
        });
        guide.hidden = false;
        guide.dataset.ready = 'true';
      }
    };
    dialog?.addEventListener('close', () => {
      document.documentElement.classList.remove('dm-hw-dialog-open');
      if (!window.dmOverlay?.current() && lastTrigger?.isConnected) lastTrigger.focus({preventScroll: true});
    });
    dialog?.addEventListener('click', (event) => {
      const bounds = dialog.getBoundingClientRect();
      if (event.target === dialog && (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom)) dialog.close();
    });
    root.querySelectorAll('[data-hw-close]').forEach((button) => button.addEventListener('click', () => dialog.close()));
    root.addEventListener('click', (event) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
      const trigger = event.target.closest('[data-hw-open]');
      if (!trigger) return;
      event.preventDefault();
      const index = Number(trigger.dataset.hwOpen);
      openDetails(index, trigger);
    });
    products.forEach((product, index) => {
      const thumbnails = Array.from(product.querySelectorAll('[data-hw-thumbnail]'));
      const photos = Array.from(product.querySelectorAll('[data-hw-photo]'));
      thumbnails.forEach((thumbnail, photoIndex) => thumbnail.addEventListener('click', () => {
        thumbnails.forEach((button, i) => button.setAttribute('aria-pressed', String(i === photoIndex)));
        photos.forEach((photo, i) => { photo.hidden = i !== photoIndex; });
      }));
      const form = product.querySelector('[data-hw-form]');
      const submit = form.querySelector('[data-hw-add]');
      const status = form.querySelector('[data-hw-form-status]');
      const bag = form.querySelector('[data-hw-view-bag]');
      form.addEventListener('change', () => {
        const variant = form.querySelector('input[name="id"]:checked');
        if (!variant || variant.disabled) return;
        product.querySelector('[data-hw-price]').textContent = variant.dataset.hwPriceLabel;
        submit.disabled = false;
        submit.textContent = 'Agregar a mi bolsa';
        status.textContent = '';
        bag.hidden = true;
      });
      const showBag = async () => {
        await window.dmCart.refresh();
        dialog.close();
        if (window.dmOverlay.current() !== 'dm-cart') window.dmOverlay.show('dm-cart', lastTrigger);
        else document.querySelector('#dm-cart button')?.focus({preventScroll: true});
      };
      bag.addEventListener('click', async () => {
        bag.disabled = true;
        try { await showBag(); }
        catch { status.textContent = 'Tu ruana ya está en la bolsa. No pudimos cargarla; inténtalo de nuevo.'; }
        finally { bag.disabled = false; }
      });
      form.addEventListener('submit', async (event) => {
        // Preserve the native product form if the theme's cart integration is unavailable.
        if (!window.dmCart || !window.dmOverlay) return;
        event.preventDefault();
        const variant = form.querySelector('input[name="id"]:checked');
        if (!variant || variant.disabled || form.dataset.busy === 'true') return;
        form.dataset.busy = 'true';
        form.setAttribute('aria-busy', 'true');
        form.querySelector('fieldset').disabled = true;
        submit.disabled = true;
        submit.textContent = 'Agregando a tu bolsa…';
        status.textContent = '';
        let added = false;
        try {
          await window.dmCart.add([{id: variant.value, quantity: 1}]);
          added = true;
          submit.textContent = 'En tu bolsa ✓';
          status.textContent = 'Tu ruana ya está en la bolsa.';
          bag.hidden = false;
          // The existing theme also opens the cart after add; release the native dialog first.
          dialog.close();
          try { await showBag(); }
          catch {
            openDetails(index, lastTrigger);
            status.textContent = 'Tu ruana ya está en la bolsa. Puedes volver a intentar abrirla.';
          }
        } catch (error) {
          status.textContent = error.message || 'No pudimos agregarla. Intenta de nuevo.';
          submit.textContent = 'Intentar de nuevo';
        } finally {
          form.dataset.busy = 'false';
          form.removeAttribute('aria-busy');
          form.querySelector('fieldset').disabled = false;
          submit.disabled = added;
        }
      });
    });
    buttons.forEach((button, i) => {
      button.addEventListener('animationend', (event) => {
        if (event.animationName === 'dm-hw-cloth-settle') button.classList.remove('is-arriving');
      });
      button.addEventListener('click', () => openDetails(i, button));
      button.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse' && !mobile.matches) select(i, false);
      });
      button.addEventListener('focus', () => select(i));
      button.addEventListener('keydown', (event) => {
        const keys = {ArrowRight: (selected + 1) % buttons.length, ArrowLeft: (selected - 1 + buttons.length) % buttons.length, Home: 0, End: buttons.length - 1};
        if (!(event.key in keys)) return;
        event.preventDefault();
        select(keys[event.key]);
        buttons[selected].focus({preventScroll: true});
      });
    });
    root.querySelector('[data-hw-prev]')?.addEventListener('click', () => select(selected - 1));
    root.querySelector('[data-hw-next]')?.addEventListener('click', () => select(selected + 1));
    steps.forEach((step) => step.addEventListener('click', () => select(Number(step.dataset.hwGo))));
    let settleTimer;
    rail.addEventListener('scroll', () => {
      if (!mobile.matches) return;
      if (scrollingToSelection) {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => { scrollingToSelection = false; }, 150);
        return;
      }
      postponeAuto();
      clearTimeout(settleTimer);
      settleTimer = setTimeout(() => {
        const middle = rail.scrollLeft + rail.clientWidth / 2;
        const nearest = buttons.reduce((best, button, i) => Math.abs(button.offsetLeft + button.offsetWidth / 2 - middle) < Math.abs(buttons[best].offsetLeft + buttons[best].offsetWidth / 2 - middle) ? i : best, 0);
        if (nearest !== selected) select(nearest, false);
      }, 100);
    }, {passive:true});
    mobile.addEventListener('change', () => center('instant'));
    // Shopify theme-editor selection should reveal the selected product.
    root.addEventListener('shopify:block:select', (event) => {
      const button = event.target.closest('[data-hw-select]');
      if (button) select(Number(button.dataset.hwSelect));
    });
    // Rotate gently, while keeping manual browsing and product details in control.
    const autoplay = root.querySelector('[data-hw-autoplay]');
    if (autoplay && buttons.length > 1) {
      const interval = 3500;
      const hero = root.querySelector('.dm-hw__hero');
      const rack = root.querySelector('[data-hw-rack]');
      const events = new AbortController();
      const hovered = new Set();
      let paused = reduceMotion.matches;
      let visible = false;
      let keyboardFocus = false;
      let autoTimer;
      let progressAnimation;
      let holdUntil = 0;
      const renderPlayback = () => {
        autoplay.querySelector('[data-hw-autoplay-label]').textContent = paused ? 'Reproducir' : 'Pausar';
        autoplay.setAttribute('aria-label', paused ? 'Reproducir cambio automático de ruanas' : 'Pausar cambio automático de ruanas');
        autoplay.title = paused ? 'Reproducir' : 'Pausar';
        autoplay.dataset.state = paused ? 'paused' : 'playing';
      };
      const blocked = () => paused || !visible || document.hidden || dialog?.open || hovered.size > 0 || keyboardFocus || !root.isConnected;
      const schedule = () => {
        clearTimeout(autoTimer);
        progressAnimation?.cancel();
        progressAnimation = undefined;
        root.dataset.hwRotating = 'false';
        if (blocked()) return;
        const delay = Math.max(interval, holdUntil - Date.now());
        const fill = steps[selected]?.querySelector('[data-hw-progress-fill]');
        root.dataset.hwRotating = 'true';
        if (fill && !reduceMotion.matches) {
          progressAnimation = fill.animate([{transform: 'scaleX(0)'}, {transform: 'scaleX(1)'}], {duration: delay, easing: 'linear', fill: 'forwards'});
        }
        autoTimer = setTimeout(() => {
          if (blocked()) { schedule(); return; }
          if (window.dmOverlay?.current()) { schedule(); return; }
          select(selected + 1, true, true);
          schedule();
        }, delay);
      };
      syncPlayback = schedule;
      postponeAuto = () => { holdUntil = Date.now() + 8000; schedule(); };
      autoplay.hidden = false;
      renderPlayback();
      autoplay.addEventListener('click', () => {
        paused = !paused;
        holdUntil = 0;
        keyboardFocus = false;
        renderPlayback();
        schedule();
      }, {signal: events.signal});
      [rail, details].filter(Boolean).forEach((area) => {
        area.addEventListener('pointerenter', (event) => {
          if (event.pointerType === 'mouse' && !mobile.matches) { hovered.add(area); schedule(); }
        }, {signal: events.signal});
        area.addEventListener('pointerleave', () => { hovered.delete(area); schedule(); }, {signal: events.signal});
      });
      hero.addEventListener('pointerdown', (event) => {
        if (event.target.closest('[data-hw-autoplay]')) return;
        if (event.target.closest('[data-hw-hangers]')) {
          scrollingToSelection = false;
          clearTimeout(scrollTimer);
          rail.scrollTo({left: rail.scrollLeft, behavior: 'instant'});
        }
        postponeAuto();
      }, {signal: events.signal});
      hero.addEventListener('focusin', (event) => {
        keyboardFocus = !event.target.closest('[data-hw-autoplay]') && event.target.matches(':focus-visible');
        schedule();
      }, {signal: events.signal});
      hero.addEventListener('focusout', () => {
        queueMicrotask(() => {
          keyboardFocus = hero.contains(document.activeElement) && !document.activeElement.closest('[data-hw-autoplay]') && document.activeElement.matches(':focus-visible');
          schedule();
        });
      }, {signal: events.signal});
      dialog?.addEventListener('close', postponeAuto, {signal: events.signal});
      document.addEventListener('visibilitychange', schedule, {signal: events.signal});
      reduceMotion.addEventListener('change', () => { paused = reduceMotion.matches; renderPlayback(); schedule(); }, {signal: events.signal});
      mobile.addEventListener('change', () => { hovered.clear(); schedule(); }, {signal: events.signal});
      const observer = new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; schedule(); }, {threshold: 0.15});
      observer.observe(rack);
      document.addEventListener('shopify:section:unload', (event) => {
        if (!event.target.contains(root)) return;
        clearTimeout(autoTimer);
        progressAnimation?.cancel();
        clearTimeout(scrollTimer);
        clearTimeout(settleTimer);
        observer.disconnect();
        events.abort();
      }, {signal: events.signal});
    }
    requestAnimationFrame(() => center('instant'));
  };
  const init = (scope) => scope.querySelectorAll('[data-dm-halloween]').forEach(mount);
  init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
